import { css, Grid, Stack, Typography } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useContext, useMemo } from 'react'
import { match } from 'ts-pattern'
import Crosshairs from 'assets/icons/nucleo/18px_crosshairs.svg'
import Location from 'assets/icons/nucleo/18px_location.svg'
import { unifiedColors } from 'styles/colors'
import { AutocompleteResultRow } from './AutocompleteResultRow'
import { Option } from './LocationTabContent'
import { SearchBarContext } from './SearchBarContext'

const locationsFallback: Option[] = [
  {
    id: 'bordeaux',
    name: 'Bordeaux',
    'feature code': 'PPLA',
    'admin2 code': '33',
    _geo: {
      lat: 44.8378,
      lng: -0.5792
    }
  },
  {
    id: 'lille',
    name: 'Lille',
    'feature code': 'PPLA',
    'admin2 code': '59',
    _geo: {
      lat: 50.6292,
      lng: 3.0573
    }
  },
  {
    id: 'lyon',
    name: 'Lyon',
    'feature code': 'PPLA',
    'admin2 code': '69',
    _geo: {
      lat: 45.764,
      lng: 4.8357
    }
  },
  {
    id: 'marseille',
    name: 'Marseille',
    'feature code': 'PPLA',
    'admin2 code': '13',
    _geo: {
      lat: 43.2965,
      lng: 5.3698
    }
  },
  {
    id: 'nantes',
    name: 'Nantes',
    'feature code': 'PPLA',
    'admin2 code': '44',
    _geo: {
      lat: 47.2184,
      lng: -1.5536
    }
  },
  {
    id: 'paris',
    name: 'Paris',
    'feature code': 'PPLA',
    'admin2 code': '75',
    _geo: {
      lat: 48.8566,
      lng: 2.3522
    }
  }
]

export const LocationAutocomplete = () => {
  const { t } = useTranslation(['common', 'home'])
  const {
    locationAutocompleteState,
    locationSearch,
    setLocationSearch,
    setLocation,
    locationAutocomplete: autocomplete
  } = useContext(SearchBarContext)

  const numberOfColumns = locationSearch ? 1 : 2

  const handleSetAroundMe = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setLocationSearch('')
        setLocation({
          id: 'around-me',
          name: t('header.aroundMe'),
          _geo: {
            lat: latitude,
            lng: longitude
          }
        })
      },
      (error) => {
        console.error('Error getting location:', error)
      }
    )
  }

  const handleSetLocation = (item: Option | undefined) => {
    setLocationSearch('')
    setLocation(item)
  }

  const displayedCollections: Array<Option & { department?: string }> =
    useMemo(() => {
      if (!locationAutocompleteState) return []

      const { suggestedLocations, locations, departments } =
        locationAutocompleteState.collections.reduce(
          (acc, { source: { sourceId }, items }) => {
            match(sourceId)
              .with('suggestedLocations', () => {
                acc.suggestedLocations = items as Option[]
              })
              .with('locations', () => {
                acc.locations = items as Option[]
              })
              .with('departments', () => {
                acc.departments = items as Option[]
              })
              .otherwise(() => {})
            return acc
          },
          {
            suggestedLocations: [] as Option[],
            locations: [] as Option[],
            departments: [] as Option[]
          }
        )

      const results = locationSearch
        ? locations
        : suggestedLocations.length > 0
        ? suggestedLocations
        : locationsFallback

      return results.map((item) => ({
        ...item,
        department: departments.find(
          (department) => department['admin2 code'] === item['admin2 code']
        )?.name
      }))
    }, [locationAutocompleteState?.collections])

  if (displayedCollections.length === 0) {
    return (
      <Stack width="100%" p={2} justifyContent="center" alignItems="center">
        <Typography variant="body2">{t('home:head.noResults')}</Typography>
      </Stack>
    )
  }

  return (
    <Stack className="aa-Autocomplete" {...autocomplete.getRootProps({})}>
      <Grid container spacing={{ md: numberOfColumns }}>
        {numberOfColumns > 1 && (
          <Grid size={{ md: 6 }} width="100%">
            <AutocompleteResultRow
              background="green50"
              iconBackground="green"
              Icon={() => (
                <Crosshairs
                  css={css`
                    path {
                      stroke: ${unifiedColors.label.onGreen};
                    }
                  `}
                />
              )}
              item={null}
              setItem={handleSetAroundMe}
              value={t('header.aroundMe')}
            />
          </Grid>
        )}
        <Grid size={{ md: 12 / numberOfColumns }} width="100%">
          {!locationSearch && (
            <Stack
              flexDirection="row"
              padding="12px 16px 8px"
              gap="13px"
              alignItems="center"
              borderRadius="16px"
            >
              <Typography variant="body2" fontSize={16} fontWeight={600}>
                {t('header.suggestions')}
              </Typography>
            </Stack>
          )}
          {displayedCollections.map((item) => (
            <AutocompleteResultRow
              key={item.id}
              background="purple50"
              iconBackground="purple"
              Icon={() => (
                <Location
                  css={css`
                    path {
                      stroke: ${unifiedColors.label.onPurple};
                    }
                  `}
                />
              )}
              item={item}
              setItem={handleSetLocation}
              value={
                <Stack>
                  <Typography
                    variant="body2"
                    fontSize={16}
                    color={unifiedColors.label.subdued}
                    css={css`
                      strong {
                        color: ${unifiedColors.label.default};
                      }
                    `}
                    dangerouslySetInnerHTML={{
                      __html: item._highlightResult?.name.value || item.name
                    }}
                  />
                  {item.department && (
                    <Typography
                      variant="body2"
                      fontSize={14}
                      color={unifiedColors.label.subduedDark}
                    >
                      {item.department} ({item['admin2 code']})
                    </Typography>
                  )}
                </Stack>
              }
            />
          ))}
        </Grid>
      </Grid>
    </Stack>
  )
}
